/**
 * Cliente de la API DOOH (tag `dooh` de `openapi.yaml`) — Admin Web (US 2.6).
 *
 * Endpoints admin (requieren JWT del backoffice y rol):
 * - Medios (SUPER_ADMIN / MARKETING), TG-58:
 *   POST   /api/v1/dooh/media        (multipart, campo `file`)
 *   GET    /api/v1/dooh/media
 *   GET    /api/v1/dooh/media/{id}
 *   DELETE /api/v1/dooh/media/{id}
 * - Pantallas (SUPER_ADMIN / SOPORTE_N3), TG-59:
 *   GET    /api/v1/dooh/screens
 *   GET    /api/v1/dooh/screens/{id}
 *   PATCH  /api/v1/dooh/screens/{id}
 *   GET    /api/v1/dooh/screens/{id}/status
 * - Emisión global (SUPER_ADMIN / MARKETING), TG-59:
 *   GET    /api/v1/dooh/broadcast
 *   PUT    /api/v1/dooh/broadcast
 *
 * La subida multipart reutiliza `apiRequest` (JWT/refresh ya resueltos): el cliente
 * detecta `FormData` y NO fija `Content-Type` para no romper el boundary.
 */
import type {
  DoohBroadcastResponse,
  DoohMedia,
  DoohMediaListResponse,
  DoohScreen,
  DoohScreenListResponse,
  DoohScreenStatusResponse,
  SetDoohBroadcastBody,
  UpdateDoohScreenBody,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

/** Campo multipart del contrato para subir un medio (`POST /dooh/media`). */
export const DOOH_UPLOAD_FIELD = 'file';

export const doohApi = {
  /* ---------------------------------------------------------------- Medios */

  listMedia(): Promise<DoohMediaListResponse> {
    return apiRequest<DoohMediaListResponse>('/api/v1/dooh/media', { retryOnUnauthorized: true });
  },

  getMedia(mediaId: string): Promise<DoohMedia> {
    return apiRequest<DoohMedia>(`/api/v1/dooh/media/${encodeURIComponent(mediaId)}`);
  },

  /** Sube el máster JPG/MP4 (multipart, campo `file`). */
  uploadMedia(file: File): Promise<DoohMedia> {
    const form = new FormData();
    form.append(DOOH_UPLOAD_FIELD, file, file.name);
    return apiRequest<DoohMedia>('/api/v1/dooh/media', { method: 'POST', body: form });
  },

  removeMedia(mediaId: string): Promise<void> {
    return apiRequest<void>(`/api/v1/dooh/media/${encodeURIComponent(mediaId)}`, {
      method: 'DELETE',
    });
  },

  /* ------------------------------------------------------------ Pantallas */

  listScreens(): Promise<DoohScreenListResponse> {
    return apiRequest<DoohScreenListResponse>('/api/v1/dooh/screens', {
      retryOnUnauthorized: true,
    });
  },

  getScreen(screenId: string): Promise<DoohScreen> {
    return apiRequest<DoohScreen>(`/api/v1/dooh/screens/${encodeURIComponent(screenId)}`);
  },

  getScreenStatus(screenId: string): Promise<DoohScreenStatusResponse> {
    return apiRequest<DoohScreenStatusResponse>(
      `/api/v1/dooh/screens/${encodeURIComponent(screenId)}/status`,
    );
  },

  updateScreen(screenId: string, body: UpdateDoohScreenBody): Promise<DoohScreen> {
    return apiRequest<DoohScreen>(`/api/v1/dooh/screens/${encodeURIComponent(screenId)}`, {
      method: 'PATCH',
      body,
    });
  },

  /* ------------------------------------------------------- Emisión global */

  getBroadcast(): Promise<DoohBroadcastResponse> {
    return apiRequest<DoohBroadcastResponse>('/api/v1/dooh/broadcast', {
      retryOnUnauthorized: true,
    });
  },

  setBroadcast(body: SetDoohBroadcastBody): Promise<DoohBroadcastResponse> {
    return apiRequest<DoohBroadcastResponse>('/api/v1/dooh/broadcast', {
      method: 'PUT',
      body,
    });
  },
};
